/**
 * Fisher-Yates shuffle algorithm for uniform random shuffling
 * @param {Array} array - Array to shuffle
 * @returns {Array} Shuffled array
 */
const fisherYatesShuffle = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Generates matches for a given number of teams and fields
 *
 * @param {Number} numTeams - Number of teams
 * @param {Number} numFields - Number of available fields
 * @returns {Array} Array of match objects
 */
export const generateMatches = (numTeams, numFields) => {
  // Ensure we don't try to use more fields than possible matches
  const maxPossibleMatches = Math.floor(numTeams / 2);
  const fieldsToUse = Math.min(numFields, maxPossibleMatches);

  if (numTeams < 2 || fieldsToUse <= 0) {
    return [];
  }

  // Create array of team indexes
  const teamIndexes = Array.from({ length: numTeams }, (_, i) => i);

  // Shuffle team indexes for random matchups using Fisher-Yates algorithm
  const shuffledTeams = fisherYatesShuffle(teamIndexes);
  
  // Create matches (pairs of teams)
  const matches = [];
  for (let i = 0; i < fieldsToUse * 2; i += 2) {
    if (i + 1 < shuffledTeams.length) {
      matches.push({
        teamA: shuffledTeams[i],
        teamB: shuffledTeams[i + 1],
        field: Math.floor(i / 2) + 1,
        scoreA: null,
        scoreB: null
      });
    }
  }
  
  return matches;
};

/**
 * Generates a tournament schedule for all rounds
 * 
 * @param {Number} numTeams - Number of teams
 * @param {Number} numRounds - Number of rounds
 * @param {Number} numFields - Number of available fields
 * @returns {Array} Array of round schedules
 */
export const generateTournamentSchedule = (numTeams, numRounds, numFields) => {
  const rounds = [];
  
  for (let i = 0; i < numRounds; i++) {
    const roundMatches = generateMatches(numTeams, numFields);
    rounds.push(roundMatches);
  }
  
  return rounds;
};

/**
 * Updates the score for a match
 * 
 * @param {Object} match - Match object
 * @param {Number} scoreA - Score for team A
 * @param {Number} scoreB - Score for team B
 * @returns {Object} Updated match object
 */
export const updateMatchScore = (match, scoreA, scoreB) => {
  return {
    ...match,
    scoreA: scoreA === null || scoreA === undefined ? null : parseInt(scoreA, 10),
    scoreB: scoreB === null || scoreB === undefined ? null : parseInt(scoreB, 10)
  };
};

/**
 * Gets the team name from the team index
 * 
 * @param {Number} teamIndex - Team index
 * @returns {String} Team name (A, B, C, etc.)
 */
export const getTeamName = (teamIndex) => {
  return String.fromCharCode(65 + teamIndex); // A, B, C, etc.
};

/**
 * Gets the result status of a match
 * 
 * @param {Object} match - Match object
 * @returns {String} Result status
 */
export const getMatchResult = (match) => {
  if (match.scoreA === null || match.scoreB === null) {
    return 'pending';
  }
  if (match.scoreA > match.scoreB) {
    return 'teamA';
  }
  if (match.scoreA < match.scoreB) {
    return 'teamB';
  }
  return 'draw';
};

/**
 * Calculates statistics for each team based on match results
 * 
 * @param {Array} schedule - Tournament schedule
 * @returns {Object} Team statistics
 */
export const calculateTeamStats = (schedule) => {
  const stats = {};
  
  // Process all rounds and matches
  schedule.forEach(round => {
    round.forEach(match => {
      // Skip matches without results
      if (match.scoreA === null || match.scoreB === null) return;
      
      // Initialize team stats if needed
      if (!stats[match.teamA]) {
        stats[match.teamA] = { played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0 };
      }
      if (!stats[match.teamB]) {
        stats[match.teamB] = { played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0 };
      }
      
      // Update match count
      stats[match.teamA].played++;
      stats[match.teamB].played++;
      
      // Update goals
      stats[match.teamA].goalsFor += match.scoreA;
      stats[match.teamA].goalsAgainst += match.scoreB;
      stats[match.teamB].goalsFor += match.scoreB;
      stats[match.teamB].goalsAgainst += match.scoreA;
      
      // Update result stats
      if (match.scoreA > match.scoreB) {
        // Team A won
        stats[match.teamA].won++;
        stats[match.teamA].points += 3;
        stats[match.teamB].lost++;
      } else if (match.scoreA < match.scoreB) {
        // Team B won
        stats[match.teamB].won++;
        stats[match.teamB].points += 3;
        stats[match.teamA].lost++;
      } else {
        // Draw
        stats[match.teamA].drawn++;
        stats[match.teamB].drawn++;
        stats[match.teamA].points += 1;
        stats[match.teamB].points += 1;
      }
    });
  });
  
  return stats;
};

/**
 * Creates a match schedule for each round based on team order and available fields
 * @param {Array} teams - Array of rounds, each with array of team arrays
 * @param {Number} numFields - Number of available fields
 * @returns {Array} Array of rounds with match objects
 */
export const createMatchSchedule = (teams, numFields) => {
  return teams.map(roundTeams => {
    const numTeams = roundTeams.length;
    // Ensure we don't try to use more fields than possible matches
    const maxPossibleMatches = Math.floor(numTeams / 2);
    const fieldsToUse = Math.min(numFields, maxPossibleMatches);
    
    if (numTeams < 2 || fieldsToUse <= 0) {
      return [];
    }

    // Create matches (pairs of teams)
    const matches = [];
    
    // Instead of shuffling team indexes, maintain consistent pairings by field
    // For example, Field 1 always has Team A vs Team B, Field 2 always has Team C vs Team D, etc.
    for (let i = 0; i < fieldsToUse; i++) {
      const teamAIndex = i * 2;
      const teamBIndex = i * 2 + 1;
      
      if (teamBIndex < numTeams) {
        matches.push({
          teamA: teamAIndex,
          teamB: teamBIndex,
          field: i + 1,
          scoreA: null,
          scoreB: null
        });
      }
    }
    
    return matches;
  });
};

/**
 * Calculates field capacity validation and match distribution statistics
 * @param {Number} numTeams - Number of teams
 * @param {Number} numFields - Number of available fields  
 * @param {Number} numRounds - Number of rounds
 * @returns {Object} Validation results and statistics
 */
export const validateFieldCapacity = (numTeams, numFields, numRounds) => {
  const maxSimultaneousMatches = Math.floor(numTeams / 2);
  const requiredFields = maxSimultaneousMatches;
  const hasInsufficientFields = requiredFields > numFields;
  
  // Calculate how many teams can play per round with current fields
  const teamsPlayingPerRound = numFields * 2;
  const teamsWaitingPerRound = Math.max(0, numTeams - teamsPlayingPerRound);
  
  // Calculate match distribution
  const totalPossibleMatches = numFields * numRounds;
  const idealMatchesPerTeam = Math.floor((totalPossibleMatches * 2) / numTeams);
  const remainingMatches = (totalPossibleMatches * 2) % numTeams;
  
  // Calculate actual matches per team considering field constraints
  const matchesPerTeamMin = Math.floor(idealMatchesPerTeam);
  const matchesPerTeamMax = remainingMatches > 0 ? matchesPerTeamMin + 1 : matchesPerTeamMin;
  const teamsWithExtraMatch = remainingMatches;
  
  return {
    hasInsufficientFields,
    requiredFields,
    availableFields: numFields,
    maxSimultaneousMatches,
    teamsPlayingPerRound,
    teamsWaitingPerRound,
    totalPossibleMatches,
    idealMatchesPerTeam,
    matchesPerTeamMin,
    matchesPerTeamMax,
    teamsWithExtraMatch,
    canAllTeamsPlaySimultaneously: !hasInsufficientFields
  };
};

/**
 * Creates an improved match schedule that distributes matches more evenly when fields are limited
 * @param {Array} teams - Array of rounds, each with array of team arrays
 * @param {Number} numFields - Number of available fields
 * @returns {Array} Array of rounds with match objects, optimized for even distribution
 */
export const createImprovedMatchSchedule = (teams, numFields) => {
  const numTeams = teams[0]?.length || 0;
  const numRounds = teams.length;
  
  if (numTeams < 2 || numFields < 1) {
    return [];
  }
  
  const maxMatchesPerRound = Math.floor(numTeams / 2);
  const fieldsToUse = Math.min(numFields, maxMatchesPerRound);
  
  // Track how many matches each team has played
  const teamMatchCounts = Array(numTeams).fill(0);
  const schedule = [];
  
  for (let roundIndex = 0; roundIndex < numRounds; roundIndex++) {
    const roundMatches = [];
    const availableTeams = Array(numTeams).fill().map((_, i) => i);
    
    // Sort teams by match count (ascending) to prioritize teams with fewer matches
    availableTeams.sort((a, b) => teamMatchCounts[a] - teamMatchCounts[b]);
    
    let fieldCount = 0;
    
    // Create matches for this round, prioritizing teams with fewer total matches
    while (fieldCount < fieldsToUse && availableTeams.length >= 2) {
      const teamA = availableTeams.shift(); // Team with fewest matches
      const teamB = availableTeams.shift(); // Team with second fewest matches
      
      roundMatches.push({
        teamA,
        teamB,
        field: fieldCount + 1,
        scoreA: null,
        scoreB: null
      });
      
      // Update match counts
      teamMatchCounts[teamA]++;
      teamMatchCounts[teamB]++;
      fieldCount++;
    }
    
    schedule.push(roundMatches);
  }
  
  return schedule;
};

/**
 * Creates a balanced match schedule with simple cost balancing for opponents and fields.
 * - Minimizes opponent repeats by tracking previous matchups across rounds
 * - Balances fields by penalizing players who overuse a field
 * This is a greedy heuristic to keep dependencies small.
 * @param {Array} teams - Array of rounds, each with array of team arrays (players' names)
 * @param {Number} numFields - Number of available fields
 * @param {Array} previousSchedule - (optional) schedule up to previous rounds to track repeats
 * @returns {Array} Array of rounds with match objects
 */
export const createBalancedMatchSchedule = (teams, numFields, previousSchedule = []) => {
  const numRounds = teams.length;
  const schedule = [];

  // Track opponent meetings: key "a-b" with a<b
  const met = new Map();
  const key = (a, b) => (a < b ? `${a}-${b}` : `${b}-${a}`);

  // Track per-player field usage across rounds (for mild balancing)
  const fieldUsage = new Map(); // playerName -> Map(field->count)
  const bumpField = (player, field) => {
    if (!fieldUsage.has(player)) fieldUsage.set(player, new Map());
    const m = fieldUsage.get(player);
    m.set(field, (m.get(field) || 0) + 1);
  };

  // Track field usage per opponent pair to directly balance A/B, C/D, etc.
  const pairFieldUsage = new Map(); // key(a,b) -> Map(field->count)
  const bumpPairField = (a, b, field) => {
    const k = key(a, b);
    if (!pairFieldUsage.has(k)) pairFieldUsage.set(k, new Map());
    const m = pairFieldUsage.get(k);
    m.set(field, (m.get(field) || 0) + 1);
  };
  const getPairFieldCount = (a, b, field) => pairFieldUsage.get(key(a, b))?.get(field) || 0;

  // Seed from previous schedule if provided
  for (const round of (previousSchedule || [])) {
    for (const m of (round || [])) {
      met.set(key(m.teamA, m.teamB), (met.get(key(m.teamA, m.teamB)) || 0) + 1);
      // Seed pair-field usage as well
      if (m.field) bumpPairField(m.teamA, m.teamB, m.field);
      // cannot seed player fieldUsage without player names here
    }
    schedule.push(round);
  }

  // Helper to compute opponent repeat count between team indices in a round
  const opponentRepeat = (a, b) => met.get(key(a, b)) || 0;

  // Build each round greedily
  for (let r = previousSchedule.length; r < numRounds; r++) {
    const roundTeams = teams[r] || [];
    const nTeams = roundTeams.length;
    const maxMatchesPerRound = Math.floor(nTeams / 2);
    const fieldsToUse = Math.min(numFields, maxMatchesPerRound);
    const unusedTeams = Array.from({ length: nTeams }, (_, i) => i);

    const roundMatches = [];

    while (roundMatches.length < fieldsToUse && unusedTeams.length >= 2) {
      // Pick anchor: team with smallest number of already scheduled matches this round (just pick first)
      const a = unusedTeams.shift();
      // Choose partner that minimizes opponent repeat; tie-break by approximate field balance cost
      let bestIdx = -1;
      let bestCost = Infinity;
      for (let j = 0; j < unusedTeams.length; j++) {
        const b = unusedTeams[j];
        const rep = opponentRepeat(a, b);
        // Estimate minimal possible field cost for this pair by checking all fields
        let minFieldCost = Infinity;
        for (let f = 1; f <= fieldsToUse; f++) {
          let fieldCost = 0;
          // Pair field usage is the primary balancing target
          fieldCost += getPairFieldCount(a, b, f) * 10; // strong weight to spread pair across fields
          // Mild per-player field balancing as secondary
          for (const p of (roundTeams[a] || [])) fieldCost += (fieldUsage.get(p)?.get(f) || 0) * 0.5;
          for (const p of (roundTeams[b] || [])) fieldCost += (fieldUsage.get(p)?.get(f) || 0) * 0.5;
          if (fieldCost < minFieldCost) minFieldCost = fieldCost;
        }
        const cost = rep * 10 + minFieldCost; // opponent repeats dominate; then best attainable field spread
        if (cost < bestCost) {
          bestCost = cost;
          bestIdx = j;
        }
      }
      const b = unusedTeams.splice(bestIdx, 1)[0];
      // Choose the actual field for this pair: least used by this pair (break ties by current load)
      let chosenField = 1;
      let bestFieldScore = Infinity;
      for (let f = 1; f <= fieldsToUse; f++) {
        const pairCount = getPairFieldCount(a, b, f);
        // also consider how many matches already assigned this round to field f
        const fieldLoad = roundMatches.filter(m => m.field === f).length;
        // and mild per-player usage to avoid extremes
        let playerCost = 0;
        for (const p of (roundTeams[a] || [])) playerCost += (fieldUsage.get(p)?.get(f) || 0) * 0.5;
        for (const p of (roundTeams[b] || [])) playerCost += (fieldUsage.get(p)?.get(f) || 0) * 0.5;
        const score = pairCount * 100 + fieldLoad * 5 + playerCost; // strong priority to pair spread
        if (score < bestFieldScore) {
          bestFieldScore = score;
          chosenField = f;
        }
      }
      roundMatches.push({ teamA: a, teamB: b, field: chosenField, scoreA: null, scoreB: null });
      met.set(key(a, b), (met.get(key(a, b)) || 0) + 1);
      // Update field usage tracking
      bumpPairField(a, b, chosenField);
      for (const p of (roundTeams[a] || [])) bumpField(p, chosenField);
      for (const p of (roundTeams[b] || [])) bumpField(p, chosenField);
    }

    schedule.push(roundMatches);
  }

  return schedule;
};